import React, { useEffect, useState } from 'react';

import Checkbox from 'shared/Checkbox';
import { LOTS_COFFEE_TYPE_CHANGE, LotCoffeeTypeDetail } from './CoffeeTypeSelect';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import Grid from 'components/elements/Grid';
import InputGroupComponent from 'components/groups/InputGroup';
import { VARIANT } from 'constants/inputTypes';

export interface PaymentProps {
  initialShow: boolean;
  hasPrepayment: boolean;
  hasWetCoffeeFinalPrice: string;
  askPaidAtExcelso: boolean;
  paidAtExcelso: boolean | null;
}

function Payment({
  initialShow,
  hasPrepayment,
  hasWetCoffeeFinalPrice,
  askPaidAtExcelso,
  paidAtExcelso,
}: PaymentProps): JSX.Element | null {
  const [isWetReception, setIsWetReception] = useState(initialShow);
  const [wetCoffeeFinalPrice, setValue] = useState(hasWetCoffeeFinalPrice || '');
  // PLA-1090: null = el usuario todavia no eligio, que es lo que hace disparar el `required`.
  const [paidAtExcelsoChoice, setPaidAtExcelsoChoice] = useState<boolean | null>(
    paidAtExcelso
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  useEffect(() => {
    window.ReactEventBus.on<LotCoffeeTypeDetail>(
      LOTS_COFFEE_TYPE_CHANGE,
      ({ detail }) => {
        // `isGreen` en CoffeeTypeSelect significa wet_reception, no "verde".
        setIsWetReception(detail.coffeeType.isGreen);
      }
    );
  }, []);

  // PLA-1090: en mojado el pago en excelso se deriva del pais y no se pregunta.
  // `askPaidAtExcelso` ya trae resuelto "el pais lo pregunta Y el lote sigue pendiente de
  // pago": un lote que ya acepto precio no muestra los radios ni en gris.
  const showExcelsoChoice = !isWetReception && askPaidAtExcelso;

  if (!isWetReception && !showExcelsoChoice) return null;

  return (
    <FieldSetWithTitle title={I18n.translate('attributes.payment')}>
      <Grid>
        {isWetReception && (
          <>
            <input type='hidden' name='lot[has_prepayment]' value='' />

            <Checkbox
              checked={hasPrepayment}
              name='lot[has_prepayment]'
              id='lot_prepayment'
              data-cy='lot-prepayment'
              label={I18n.translate('attributes.prepayment')}
              value='has_prepayment'
            />

            <InputGroupComponent
              label={I18n.translate('attributes.price')}
              step={0.01}
              variant={VARIANT.DECIMAL}
              value={wetCoffeeFinalPrice}
              onChange={handleOnChange}
              name='lot[wet_coffee_final_price]'
              id='lot_wet_coffee_final_price'
              append={'US$'}
              min={0}
              required={true}
            />
          </>
        )}

        {showExcelsoChoice && (
          <>
            <Checkbox
              asRadioButton={true}
              round={true}
              icon='tick'
              inline={true}
              required={true}
              name='lot[paid_at_excelso]'
              id='lot_paid_at_excelso_false'
              value='false'
              dataCy='lot-paid-at-parchment'
              checked={paidAtExcelsoChoice === false}
              onChange={() => setPaidAtExcelsoChoice(false)}
              label={I18n.translate('attributes.payment_in_parchment')}
            />

            <Checkbox
              asRadioButton={true}
              round={true}
              icon='tick'
              inline={true}
              required={true}
              name='lot[paid_at_excelso]'
              id='lot_paid_at_excelso_true'
              value='true'
              dataCy='lot-paid-at-excelso'
              checked={paidAtExcelsoChoice === true}
              onChange={() => setPaidAtExcelsoChoice(true)}
              label={I18n.translate('attributes.payment_in_excelso')}
            />
          </>
        )}
      </Grid>
    </FieldSetWithTitle>
  );
}

export default Payment;
